import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { LineChart, BarChart } from 'echarts/charts';
import {
    DatasetComponent,
    GridComponent,
    LegendComponent,
    ToolboxComponent,
    TooltipComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import useToken from 'hooks/useToken';
import { Card } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';

echarts.use([
    DatasetComponent,
    TooltipComponent,
    ToolboxComponent,
    GridComponent,
    LegendComponent,
    LineChart,
    BarChart,
    CanvasRenderer
]);

const NetIncomeChart = ({ idUsuario, height }) => {
    const { token, setToken, idCatTipoUsuario } = useToken();
    const [cargando, setCargando] = useState(true);
    const [chartData, setChartData] = useState([]);

    const isAdmin = idCatTipoUsuario === 1 || idCatTipoUsuario === '1';

    const ObtenerNetIncome = async () => {
        if (!isAdmin) return;
        setCargando(true);
        const hoy = new Date();
        const yyyy = hoy.getFullYear();
        let mm = hoy.getMonth() + 1; // Months start at 0!
        let dd = hoy.getDate();

        if (dd < 10) dd = '0' + dd;
        if (mm < 10) mm = '0' + mm;

        const dateStr = dd + '/' + mm + '/' + yyyy;
        try {
            const response = await fetch(`/api/home/ObtenerNetIncome12Meses?idUsuario=${idUsuario}&date=${dateStr}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data['table1'][0].iResponseType === 1) {
                    setChartData(data['table2']);
                }
            } else if (response.status === 401) {
                setToken(null);
                window.location.href = "";
            }
        } catch (error) {
            console.error('Error fetching Net Income:', error);
        }
        setCargando(false);
    };

    useEffect(() => {
        ObtenerNetIncome();
    }, [idUsuario]);

    const chartOption = useMemo(() => {
        const categories = chartData.map(item => item.category);
        const values = chartData.map(item => item.netIncome);

        return {
            title: {
                text: 'Net Income (Últimos 12 Meses)',
                left: 'left',
                textStyle: { fontSize: 16, fontWeight: 'semi-bold', color: '#5e6e82' }
            },
            tooltip: {
                trigger: 'axis',
                formatter: function (params) {
                    const formatCurrency = (value) => {
                        return new Intl.NumberFormat('es-MX', {
                            style: 'currency',
                            currency: 'MXN',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                        }).format(value);
                    };

                    let result = `📅 ${params[0].axisValue}<br/>`;
                    params.forEach(param => {
                        const marker = `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${param.color};"></span>`;
                        result += `${marker}${param.seriesName}: <b>${formatCurrency(param.value)}</b><br/>`;
                    });
                    return result;
                }
            },
            grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
            xAxis: {
                type: 'category',
                data: categories,
                axisLabel: { color: '#5e6e82' },
                axisLine: { lineStyle: { color: '#d8e2ef' } }
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    color: '#5e6e82',
                    formatter: function (value) {
                        return new Intl.NumberFormat('es-MX', {
                            style: 'currency',
                            currency: 'MXN',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                            notation: 'compact',
                            compactDisplay: 'short'
                        }).format(value);
                    }
                },
                splitLine: { lineStyle: { color: '#e3ebf6' } }
            },
            series: [
                {
                    name: 'Tendencia',
                    type: 'line',
                    data: values,
                    lineStyle: { color: '#00d27a', width: 3 },
                    itemStyle: { color: '#00d27a' },
                    symbol: 'circle',
                    symbolSize: 6,
                    smooth: true,
                    zlevel: 1
                },
                {
                    name: 'Net Income',
                    type: 'bar',
                    data: values,
                    itemStyle: {
                        color: '#2c7be5',
                        borderRadius: [4, 4, 0, 0]
                    },
                    barWidth: '40%'
                }
            ]
        };
    }, [chartData]);

    if (!isAdmin) {
        return null;
    }

    return (
        <Card className="mb-3">
            <Card.Body>
                {cargando ? (
                    <Skeleton height={height} />
                ) : (
                    <ReactEChartsCore
                        echarts={echarts}
                        option={chartOption}
                        style={{ height: `${height}px`, width: '100%' }}
                    />
                )}
            </Card.Body>
        </Card>
    );
};

NetIncomeChart.propTypes = {
    idUsuario: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height: PropTypes.number
};

NetIncomeChart.defaultProps = {
    height: 350
};

export default NetIncomeChart;
